import { useLazyQuery, useQuery } from '@apollo/client';
import { Q_CREDIT_APP, QUERY_ACTIVE_OCA_TEMPLATE, QUERY_PORTAL_CONFIGURATION } from 'queries/common';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { PortalConfiguration, PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import { checkIfIndividualCreditApp } from 'utils';
import { assignFieldsOCAConfigFromActiveTemplate } from 'services/creditAppModalService';

export function useCreditAppDownloadWrapper(voId: string) {
  const vendorProfile = useSelector((state: any) => state.vp);

  const [isLoading, setIsLoading] = useState(true);
  const [CADMPortalConfiguration, setCADMPortalConfiguration] = useState<PortalConfiguration>();

  const [getActiveOcaTemplate] = useLazyQuery(QUERY_ACTIVE_OCA_TEMPLATE, {
    fetchPolicy: 'no-cache',
  });

  const [getCADMPortalConfigurations] = useLazyQuery(QUERY_PORTAL_CONFIGURATION, {
    context: { authRequired: true },
    fetchPolicy: 'no-cache',
  });

  useQuery(Q_CREDIT_APP, {
    variables: {
      VOId: voId,
    },
    fetchPolicy: 'no-cache',
    async onCompleted(creditAppData) {
      try {
        const applicationType = creditAppData?.creditApplication?.applicationType;
        const currentCreditAppOcaTemplate = creditAppData?.creditApplication?.ocaTemplate;

        let vpIdToGetCADMPortalConfigurations;
        let ocaTemplateToUse;
        if (currentCreditAppOcaTemplate) {
          // Credit app could be submitted from both the lender's and dealer's templates
          vpIdToGetCADMPortalConfigurations = currentCreditAppOcaTemplate.vendorProfileId;
          ocaTemplateToUse = currentCreditAppOcaTemplate;
        } else {
          // For OCAv3, currentCreditAppOcaTemplate will be empty, so we want to use current active OCA template
          vpIdToGetCADMPortalConfigurations = vendorProfile.id;
          const activeOcaTemplateData = await getActiveOcaTemplate({
            variables: {
              vendorProfileId: vendorProfile.id,
              isActive: true,
            },
          });
          ocaTemplateToUse = activeOcaTemplateData.data.getOcaTemplatesByVendorProfile?.[0];
        }

        const portalConfigurationData = await getCADMPortalConfigurations({
          variables: {
            vendorProfileId: vpIdToGetCADMPortalConfigurations,
            types: [
              PortalConfigurationType.CreditApplicationDetailModalCommercial,
              PortalConfigurationType.CreditApplicationDetailModalIndividual,
            ],
          },
        });
        const portalConfigurations = portalConfigurationData?.data?.portalConfigurations || [];
        const isIndividualCreditApp = checkIfIndividualCreditApp(applicationType);
        const CADMConfigurationTypeToUse = isIndividualCreditApp
          ? PortalConfigurationType.CreditApplicationDetailModalIndividual
          : PortalConfigurationType.CreditApplicationDetailModalCommercial;
        const CADMConfigurationToUse = portalConfigurations?.find((pc: any) => pc.type === CADMConfigurationTypeToUse);
        if (CADMConfigurationToUse) {
          assignFieldsOCAConfigFromActiveTemplate(
            CADMConfigurationToUse.jsonDefinition,
            ocaTemplateToUse.jsonDefinition,
            creditAppData?.creditApplication
          );
        }
        setCADMPortalConfiguration(CADMConfigurationToUse);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return {
    isLoading,
    CADMPortalConfiguration,
  };
}
